import React, { useState, useRef, useEffect } from "react";
import { API_URLS } from "../../apiUrl";
import Pagination from '@mui/material/Pagination';
import AssignedHolding from "../../popupModals/assignedHolding";
import axiosInstance from "../../components/axiosInstance";

type FormData = {
    municipality_id: string;
    page: number;
    limit: number;
};

const Reports: React.FC = () => {
    const [errors, setErrors] = useState<any>({});
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [dataExistMessage, setDataExistMessage] = useState<string>("");
    const [failedMessage, setFailedMessage] = useState<string>("");
    const [tableData, setTableData] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [surveyorId, setSurveyorId] = useState<any>("");
    const [municipalityId, setMunicipalityId] = useState<any>("");

    const handleShow = (surveyorId: any, municipalityId: any) => {
        setShowModal(true);
        setSurveyorId(surveyorId);
        setMunicipalityId(municipalityId);
    };

    const handleClose = () => setShowModal(false);

    const [inputData, setInputData] = useState({ page: 1, limit: 50 });
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage] = useState(50);

    const formRef = useRef<HTMLFormElement>(null);

    const [formData, setFormData] = useState<FormData>({
        municipality_id: "",
        page: 1,
        limit: 50
    });

    const [municipalities, setMunicipalities] = useState([]);

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: any = {};
        if (!formData.municipality_id.trim()) newErrors.municipality_id = "Municipality is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (validateForm()) {
            setLoading(true);
            try {
                const response = await axiosInstance.post(`${API_URLS.ListSurveyor}`, formData);

                if (response.data.data.data && response.data.data.data.length > 0) {
                    setTableData(response.data.data.data);
                    setTotalItems(response.data.data.total);
                    setDataExistMessage("Data successfully fetched.");
                    setTimeout(() => setDataExistMessage(""), 5000);
                } else {
                    setTableData([]);
                    setSuccessMessage("No results found for this municipality.");
                    setTimeout(() => setSuccessMessage(""), 5000);
                }
            } catch (error) {
                setFailedMessage("Failed to fetch data.");
                setTimeout(() => setFailedMessage(""), 5000);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const filteredData = tableData.filter((item: any) =>
        item.survyer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.status?.toString().includes(searchQuery.toLowerCase())
    );

    const handlePageChange = async (event: any, value: number) => {
        setInputData((prevData) => ({
            ...prevData,
            page: value,
        }));
    };

    useEffect(() => {
        const fetchData = async () => {
            if (formData.municipality_id) {
                setLoading(true);
                try {
                    const response = await axiosInstance.post(`${API_URLS.ListSurveyor}`, {
                        municipality_id: formData.municipality_id,
                        page: inputData.page,
                        limit: inputData.limit,
                    });

                    if (response.data.data.data && response.data.data.data.length > 0) {
                        setTableData(response.data.data.data);
                        setTotalItems(response.data.data.total);
                    } else {
                        setTableData([]);
                    }
                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, [inputData.page, formData.municipality_id]);

    useEffect(() => {
        const fetchMunicipalities = async () => {
            try {
                const response = await axiosInstance.post(API_URLS.MunicipalityList, {
                    page: 1,
                    limit: 100000,
                });
                setMunicipalities(response.data.data.data);
            } catch (error) {
                console.error('Error fetching municipalities:', error);
            }
        };

        fetchMunicipalities();
    }, []);

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
        <div className="max-w-full mx-auto">
            <div className="flex flex-col gap-5">
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Reports</h1>

                <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
                    <form ref={formRef} onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <div>
                                <label
                                    htmlFor="municipality_id"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    Select Municipality <span className="text-red-600">*</span>
                                </label>
                                <select
                                    id="municipality_id"
                                    name="municipality_id"
                                    value={formData.municipality_id}
                                    onChange={handleChange}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                >
                                    <option value="">Select Municipality</option>
                                    {municipalities.map((municipality: any) => (
                                        <option key={municipality.primary_id} value={municipality.primary_id}>
                                            {municipality.municipality_name}
                                        </option>
                                    ))}
                                </select>
                                {errors.municipality_id && (
                                    <p className="text-sm text-red-600 mt-1">{errors.municipality_id}</p>
                                )}
                            </div>

                            <div className="flex items-end">
                                <button
                                    type="submit"
                                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Search
                                </button>
                            </div>
                        </div>
                    </form>

                    {successMessage && (
                        <div className="mt-4 p-4 bg-yellow-600 text-white rounded-lg flex justify-between items-center">
                            <span>{successMessage}</span>
                            <button
                                onClick={() => setSuccessMessage("")}
                                className="ml-4 text-white font-bold text-lg focus:outline-none"
                            >
                                &times;
                            </button>
                        </div>
                    )}

                    {dataExistMessage && (
                        <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
                            <span>{dataExistMessage}</span>
                            <button
                                onClick={() => setDataExistMessage("")}
                                className="ml-4 text-white font-bold text-lg focus:outline-none"
                            >
                                &times;
                            </button>
                        </div>
                    )}

                    {failedMessage && (
                        <div className="mt-4 p-4 bg-red-600 text-white rounded-lg flex justify-between items-center">
                            <span>{failedMessage}</span>
                            <button
                                onClick={() => setFailedMessage("")}
                                className="ml-4 text-white font-bold text-lg focus:outline-none"
                            >
                                &times;
                            </button>
                        </div>
                    )}

                    {tableData.length > 0 && (
                        <div className="mt-6">
                            <div className="flex gap-5 mb-4">
                                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Surveyor List</h2>
                                <div className="ms-auto flex items-center max-w-sm w-full">
                                    <div className="relative w-full">
                                        <input
                                            type="text"
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                            placeholder="Search..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 dark:border-gray-800">
                                <table className="min-w-full table-auto">
                                    <thead className="bg-gray-100 border-b dark:border-gray-600 dark:bg-gray-700">
                                        <tr>
                                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Surveyor Name</th>
                                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Municipality Name</th>
                                            <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">Street Name</th>
                                            <th className="py-3 px-6 text-center text-sm font-medium text-gray-600 dark:text-gray-100">Holding No.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={4} className="py-3 px-6 text-center">Loading...</td>
                                            </tr>
                                        ) : filteredData.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="py-3 px-6 text-center dark:bg-gray-800">No results found</td>
                                            </tr>
                                        ) : (
                                            filteredData.map((item: any, index) => (
                                                <tr key={index} className="border-b dark:border-gray-700 hover:bg-gray-50">
                                                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.survyer_name || 'N/A'}</td>
                                                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.municipality_name || 'N/A'}</td>
                                                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">{item.street_names || 'N/A'}</td>
                                                    <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600 text-center">
                                                        <button 
                                                            onClick={() => handleShow(item.surveyer_id, item.municipality_id)}
                                                            className="text-blue-500 hover:text-blue-700"
                                                        >
                                                            <i className="ri-eye-fill"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {filteredData.length > 0 && (
                                <div className="flex justify-center mt-4">
                                    <Pagination
                                        count={totalPages}
                                        page={inputData.page}
                                        onChange={handlePageChange}
                                        color="primary"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <AssignedHolding 
                showModal={showModal} 
                handleClose={handleClose} 
                surveyorId={surveyorId} 
                municipalityId={municipalityId} 
            />
        </div>
    );
};

export default Reports;